import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  ArrowRight, FileSearch, AlertCircle, CheckCircle2, Send, ArrowLeftRight
} from 'lucide-react';
import { roleOptions, industryOptions, patentStatusOptions, contactMethodOptions } from '../data/mock';
import { toast } from 'sonner';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const RequestAccessPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    industry: '',
    patentStatus: '',
    contactMethod: 'email',
    description: '',
    honeypot: '' // Hidden field for bots
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Math captcha state
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0 });
  const [mathAnswer, setMathAnswer] = useState('');
  
  // Generate random math question on mount
  React.useEffect(() => {
    generateMathQuestion();
  }, []);
  
  const generateMathQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2 });
    setMathAnswer('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitted(true); // Fake success to not alert the bot
      return;
    }
    
    // Math captcha validation
    const correctAnswer = mathQuestion.num1 + mathQuestion.num2;
    if (parseInt(mathAnswer) !== correctAnswer) {
      toast.error('Incorrect answer to the math question. Please try again.');
      generateMathQuestion();
      return;
    }
    
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const templateParams = {
        form_type: 'Evaluation Request',
        from_name: formData.name,
        from_email: formData.email,
        organization: formData.organization || 'Not provided',
        role: formData.role,
        industry: formData.industry || 'Not provided',
        patent_status: formData.patentStatus || 'Not provided',
        contact_method: formData.contactMethod,
        message: formData.description || 'No additional details provided',
        to_email: 'brent@ipfunders.com'
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setIsSubmitted(true);
      toast.success('Evaluation request submitted successfully');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-20">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#0f1f2e] mb-4">
            Evaluation Request Received
          </h1>
          <p className="text-slate-600 mb-8">
            We'll review your submission and reach out within 1-2 business days. If your concept shows promise, we'll establish confidentiality and conduct deeper evaluation.
          </p>
          <div className="space-y-4">
            <Link to="/how-it-works">
              <Button variant="outline" className="w-full border-slate-300">
                Learn About Our Process
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="w-full text-slate-600">
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-20 w-64 h-64 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1e3a5f]/10 text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FileSearch className="w-4 h-4" />
            <span>Step 1: Evaluation Request</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] mb-6">
            Request Evaluation
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Submit a high-level overview of your concept. We'll assess technical merit and determine if it warrants deeper evaluation and potential funding.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 pb-24 bg-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          {/* Disclaimer */}
          <Card className="bg-amber-50 border-amber-200 mb-8">
            <CardContent className="p-5">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium mb-1">Non-Confidential Stage</p>
                  <p className="text-sm text-amber-700">
                    Do not submit confidential or enabling technical details at this stage. This form is for initial evaluation. Detailed IP information will be requested only after NDA execution.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#0f1f2e] font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0f1f2e] font-medium">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization" className="text-[#0f1f2e] font-medium">
                    Organization
                  </Label>
                  <Input
                    id="organization"
                    type="text"
                    placeholder="Company or institution name"
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0f1f2e] font-medium">
                    Your Role <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
                    <SelectTrigger className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0f1f2e] font-medium">
                    Industry Category
                  </Label>
                  <Select value={formData.industry} onValueChange={(value) => handleInputChange('industry', value)}>
                    <SelectTrigger className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]">
                      <SelectValue placeholder="Select industry (non-confidential)" />
                    </SelectTrigger>
                    <SelectContent>
                      {industryOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0f1f2e] font-medium">
                    Development Stage
                  </Label>
                  <Select value={formData.patentStatus} onValueChange={(value) => handleInputChange('patentStatus', value)}>
                    <SelectTrigger className="h-12 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]">
                      <SelectValue placeholder="Current development stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {patentStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-[#0f1f2e] font-medium">
                    Preferred Contact Method
                  </Label>
                  <RadioGroup
                    value={formData.contactMethod}
                    onValueChange={(value) => handleInputChange('contactMethod', value)}
                    className="flex flex-col space-y-3"
                  >
                    {contactMethodOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-3">
                        <RadioGroupItem value={option.value} id={option.value} />
                        <Label htmlFor={option.value} className="font-normal text-slate-600 cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[#0f1f2e] font-medium">
                    Brief Overview
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Briefly describe your concept and goals (no confidential technical details)"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="min-h-[120px] border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>

                {/* Honeypot field - hidden from humans */}
                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <Label htmlFor="company_website">Company Website</Label>
                  <Input
                    id="company_website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.honeypot}
                    onChange={(e) => handleInputChange('honeypot', e.target.value)}
                  />
                </div>

                {/* Math Captcha */}
                <div className="space-y-2">
                  <Label htmlFor="mathAnswer" className="text-[#0f1f2e] font-medium">
                    Security Check <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center space-x-3">
                    <span className="text-slate-700 font-medium bg-slate-100 px-4 py-3 rounded-lg">
                      {mathQuestion.num1} + {mathQuestion.num2} =
                    </span>
                    <Input
                      id="mathAnswer"
                      type="number"
                      placeholder="?"
                      value={mathAnswer}
                      onChange={(e) => setMathAnswer(e.target.value)}
                      className="h-12 w-24 border-slate-300 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-center"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#1e3a5f] hover:bg-[#2a4a73] text-white text-base font-semibold shadow-lg shadow-[#1e3a5f]/20 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Submit for Evaluation
                      <Send className="ml-2 w-5 h-5" />
                    </span>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Selective. NDA-first. Outcome-aligned.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Process Reminder */}
          <Card className="bg-slate-50 border-slate-200 mt-8">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <ArrowLeftRight className="w-5 h-5 text-[#1e3a5f]" />
                <span className="font-medium text-[#0f1f2e]">What happens next?</span>
              </div>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs">1</span>
                  <span>We review your submission for initial fit (1-2 business days)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs">2</span>
                  <span>If promising, we establish confidentiality via mutual NDA</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-5 h-5 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs">3</span>
                  <span>Deeper evaluation to determine funding fit</span>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RequestAccessPage;
